import React, { useState } from 'react';
import { PromptRuleService, type PromptRule } from '../services/promptRuleService';
import '../styles/create-rule-modal.css';

type PromptType = 'referral' | 'triage';

interface CreateRuleModalProps {
  onClose: () => void;
  onRuleCreated: (rule: PromptRule) => void;
  existingSections?: string[];
  promptType?: PromptType;
}

// Available fields that rules can address
const AVAILABLE_FIELDS = [
  { value: '', label: '-- Select a field --' },
  { value: 'patient.nhi', label: 'Patient - NHI' },
  { value: 'patient.first_name', label: 'Patient - First Name' },
  { value: 'patient.last_name', label: 'Patient - Last Name' },
  { value: 'patient.dob', label: 'Patient - Date of Birth' },
  { value: 'patient.phone', label: 'Patient - Phone' },
  { value: 'patient.email', label: 'Patient - Email' },
  { value: 'referral.acc_number', label: 'Referral - ACC Number' },
  { value: 'referral.reason', label: 'Referral - Reason' },
  { value: 'referral.priority', label: 'Referral - Priority' },
  { value: 'referral.body_part', label: 'Referral - Body Part' },
  { value: 'referral.body_side', label: 'Referral - Body Side' },
  { value: 'referral.date_of_injury', label: 'Referral - Date of Injury' },
  { value: 'referral.mechanism_of_injury', label: 'Referral - Mechanism of Injury' },
  { value: 'referral.clinical_notes', label: 'Referral - Clinical Notes' },
  { value: 'referral.referral_type', label: 'Referral - Referral Type' },
  { value: 'referral.is_acc_related', label: 'Referral - Is ACC Related' },
  { value: 'referral.funder', label: 'Referral - Funder' },
  { value: 'referral.imaging_performed', label: 'Referral - Imaging Performed' },
  { value: 'referral.imaging_facility', label: 'Referral - Imaging Facility' },
  { value: 'provider.name', label: 'Provider - Name' },
  { value: 'provider.organization', label: 'Provider - Organization' },
  { value: 'provider.email', label: 'Provider - Email' },
  { value: 'provider.edi', label: 'Provider - EDI' },
  { value: 'provider.phone', label: 'Provider - Phone' },
  { value: 'provider.address', label: 'Provider - Address' },
];

export const CreateRuleModal: React.FC<CreateRuleModalProps> = ({ 
  onClose, 
  onRuleCreated,
  existingSections = [],
  promptType = 'referral'
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Set default section based on prompt type
  const defaultSection = promptType === 'triage' ? 'triage_extraction' : '';
  
  const [formData, setFormData] = useState({
    section: defaultSection,
    field_name: '',
    rule_text: '',
    description: '',
    priority: 100,
    status: 'active' as const,
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'priority' ? parseInt(value, 10) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData.section.trim()) {
      setError('Section is required');
      return;
    }
    // field_name is optional, so we don't validate it
    if (!formData.rule_text.trim()) {
      setError('Rule text is required');
      return;
    }

    try {
      setIsLoading(true);
      // Add prompt_type to form data, and omit field_name if empty
      const ruleData: any = {
        section: formData.section,
        rule_text: formData.rule_text,
        description: formData.description || undefined,
        priority: formData.priority,
        status: formData.status,
        prompt_type: promptType
      };
      
      // Only include field_name if it's not empty and not the default empty option
      const fieldNameValue = formData.field_name?.trim();
      if (fieldNameValue && fieldNameValue !== '') {
        ruleData.field_name = fieldNameValue;
      }
      
      console.log('Submitting rule data:', ruleData);
      
      const newRule = await PromptRuleService.createRule(ruleData);
      console.log('Rule created successfully:', newRule);
      onRuleCreated(newRule);
      onClose();
    } catch (err: any) {
      console.error('Failed to create rule:', err);
      const errorMessage = err.message || 'Failed to create rule. Please try again.';
      console.error('Error message:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}></div>
      <div className="create-rule-modal">
        <div className="modal-header">
          <h2>Create New Prompt Rule</h2>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        {error && (
          <div className="error-message">
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="create-rule-form">
          <div className="form-group">
            <label htmlFor="section">
              Section <span className="required">*</span>
            </label>
            {promptType === 'triage' ? (
              <input
                id="section"
                name="section"
                type="text"
                value="triage_extraction"
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
            ) : (
              <select
                id="section"
                name="section"
                value={formData.section}
                onChange={handleInputChange}
                className="form-input"
                required
              >
                <option value="">Select or type section...</option>
                {existingSections.map(section => (
                  <option key={section} value={section}>
                    {section.replace(/_/g, ' ').toUpperCase()}
                  </option>
                ))}
                <option value="">─────────────</option>
                <option value="special_handling">special_handling</option>
                <option value="extraction_guidelines">extraction_guidelines</option>
                <option value="priority_rules">priority_rules</option>
                <option value="field_extraction_detailed">field_extraction_detailed</option>
                <option value="error_handling">error_handling</option>
              </select>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="field_name">
              Field Name <span>(Optional)</span>
            </label>
            <select
              id="field_name"
              name="field_name"
              value={formData.field_name}
              onChange={handleInputChange}
              className="form-input"
            >
              <option value="">-- None (Optional) --</option>
              {AVAILABLE_FIELDS.map(field => (
                <option key={field.value} value={field.value}>
                  {field.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="rule_text">
              Rule Text <span className="required">*</span>
            </label>
            <textarea
              id="rule_text"
              name="rule_text"
              value={formData.rule_text}
              onChange={handleInputChange}
              placeholder="Enter the prompt instruction text..."
              className="form-input form-textarea"
              rows={6}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (Optional)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Notes about this rule..."
              className="form-input form-textarea"
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="priority">Priority</label>
              <input
                type="number"
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                min="0"
                className="form-input"
              />
              <small>Lower numbers = higher priority (appear first)</small>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="testing">Testing</option>
              </select>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};
